import { IFieldDefFn } from "@components/smart/FieldInfo";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IEntity } from "@odata/BindingContext";
import {
    BackgroundJobEntity,
    EntitySetName,
    EntityTypeName,
    IBackgroundJobStatusEntity,
    IBackgroundJobTypeEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import { BackgroundJobStatusCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import BackgroundJobFormView from "@pages/backgroundJob/BackgroundJobFormView";
import { BackgroundJobParameters } from "@pages/backgroundJob/BackgroundJobParameters";
import { BackgroundJobResults } from "@pages/backgroundJob/BackgroundJobResults";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "@pages/PageUtils";
import { universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";
import React from "react";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { Sort, TextAlign } from "../../enums";
import { ColoredText } from "../../global.style";
import { TValue } from "../../global.types";
import { Model } from "../../model/Model";
import DateType, { DateFormat } from "../../types/Date";
import { IFormDef } from "../../views/formView/Form";
import { FormStorage } from "../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";


const statusColor = (code: BackgroundJobStatusCode) => {
    const goodStatuses = ['Finished', 'Finishing'];
    const badStatuses = ['Error', 'Canceled', 'Cancelling', 'Expired'];

    return goodStatuses.includes(code)
        ? "C_SEM_text_good"
        : badStatuses.includes(code)
            ? "C_SEM_text_bad"
            : "C_SEM_text_warning";

};

const statusFormatter = (value: TValue, args?: IFormatOptions) => {
    const color = statusColor(args.entity.StatusCode);

    return {
        tooltip: value as string,
        value: <ColoredText color={color}>{value}</ColoredText>
    };

};

const timeFormatter = (value: TValue) => {
    return DateType.localFormat(value as Date, DateFormat.dateAndTime);
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        isValueHelp: true,
        defaultFilters: [BackgroundJobEntity.Type, BackgroundJobEntity.Status, BackgroundJobEntity.DateStarted, BackgroundJobEntity.DateFinished, BackgroundJobEntity.CreatedBy],
        filterDefinition: {
            ...withDisplayName(BackgroundJobEntity.Type),
            ...withDisplayName(BackgroundJobEntity.Status),
            [BackgroundJobEntity.DateStarted]: {},
            [BackgroundJobEntity.DateFinished]: {},
            ...withDisplayName(BackgroundJobEntity.CreatedBy, {
                label: i18next.t("BackgroundJobs:Table.CreatedBy")
            })
        }
    }];

    const form: IFormDef = {
        id: `${EntityTypeName.BackgroundJob}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) => getItemBreadCrumbsText(storage, i18next.t("BackgroundJobs:Form.Title")),
        formControl: BackgroundJobFormView,
        summary: [{
            id: BackgroundJobEntity.Type,
            formatter: (val: TValue, data?: IEntity) => {
                return (val as IBackgroundJobTypeEntity)?.Name;
            },
            label: i18next.t("BackgroundJobs:Form.Type")
        }, {
            id: BackgroundJobEntity.Status,
            label: i18next.t("BackgroundJobs:Form.Status"),
            colorFormatter: (val: TValue, data?: IEntity) => {
                return statusColor((val as IBackgroundJobStatusEntity)?.Code);
            },
            formatter: (val: TValue, data?: IEntity) => {
                return (val as IBackgroundJobStatusEntity)?.Name;
            }
        }],
        isReadOnly: true,
        title: i18next.t("BackgroundJobs:Form.Title"),
        fieldDefinition: {
            [BackgroundJobEntity.DateStarted]: {
                formatter: timeFormatter
            },
            [BackgroundJobEntity.DateFinished]: {
                formatter: timeFormatter
            },
            [BackgroundJobEntity.CreatedBy]: {
                label: i18next.t("BackgroundJobs:CreatedBy"),
                additionalProperties: [{ id: UserEntity.FirstName }, { id: UserEntity.LastName }],
                formatter: (value: TValue, args?: IFormatOptions) => {
                    return universalPersonNameFormatter(args.entity.CreatedBy);
                }
            },
            [BackgroundJobEntity.Parameters]: {
                render: (args: IFieldDefFn) => {
                    return <BackgroundJobParameters storage={args.storage as FormStorage}/>;
                }
            },
            [BackgroundJobEntity.Result]: {
                render: (args: IFieldDefFn) => {
                    return <BackgroundJobResults storage={args.storage as FormStorage}/>;
                }
            }
        },
        groups: [{
            id: "Main",
            rows:
                [[{ id: BackgroundJobEntity.DateStarted }, { id: BackgroundJobEntity.DateFinished }, { id: BackgroundJobEntity.CreatedBy }]]
        }, {
            id: "Parameters",
            title: i18next.t("BackgroundJobs:Form.Parameters"),
            rows: [[{ id: BackgroundJobEntity.Parameters }]]
        }, {
            id: "Results",
            title: i18next.t("BackgroundJobs:Form.Results"),
            rows: [[{ id: BackgroundJobEntity.Result }]]
        },]
    };

    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.BackgroundJob}Table`,
        initialSortBy: [{ id: BackgroundJobEntity.DateStarted, sort: Sort.Desc }],
        columns: [BackgroundJobEntity.Type, BackgroundJobEntity.Status, BackgroundJobEntity.DateStarted, BackgroundJobEntity.DateFinished, BackgroundJobEntity.CreatedBy],
        filterBarDef,
        filter: `${BackgroundJobEntity.IsResultReadable} eq true`,
        tabs: [{
            id: null,
            title: i18next.t("BackgroundJobs:Table.All")
        }, {
            id: BackgroundJobStatusCode.Finished,
            title: i18next.t("BackgroundJobs:Table.Finished")
        }, {
            id: BackgroundJobStatusCode.FinishedWithErrors,
            title: i18next.t("BackgroundJobs:Table.FinishedWithErrors")
        }, {
            id: BackgroundJobStatusCode.PendingUserInput,
            title: i18next.t("BackgroundJobs:Table.ActionRequested")
        }, {
            id: BackgroundJobStatusCode.Error,
            title: i18next.t("BackgroundJobs:Table.NotFinished"),
            filterNames: [BackgroundJobStatusCode.Canceled, BackgroundJobStatusCode.Cancelling]
        }],
        tabsSettings: {
            filterFieldName: BackgroundJobEntity.StatusCode
        },
        title: i18next.t("BackgroundJobs:Title"),
        columnDefinition: {
            ...withDisplayName(BackgroundJobEntity.Type),
            ...withDisplayName(BackgroundJobEntity.Status, {
                formatter: statusFormatter
            }),
            [BackgroundJobEntity.DateStarted]: {
                formatter: timeFormatter,
                textAlign: TextAlign.Left
            },
            [BackgroundJobEntity.DateFinished]: {
                formatter: timeFormatter,
                textAlign: TextAlign.Left
            },
            ...withDisplayName(BackgroundJobEntity.CreatedBy, {
                label: i18next.t("BackgroundJobs:Table.CreatedBy")
            })
        }
    };

    return {
        entitySet: EntitySetName.BackgroundJobs,
        table,
        form
    };
};


getDefinitions.translationFiles = ["BackgroundJobs", "Document", "Components", getEnumNameSpaceName(EntityTypeName.CbaCategoryTaxImpact)];
setDefByEntityType(EntityTypeName.BackgroundJob, getDefinitions);
